<script setup lang="ts">
import { onMounted, type PropType } from 'vue';
import { useRouter } from 'vue-router';
import {
  Table,
  TableIconText,
  UserAvatar,
  NewEntryPopup,
  EntryCaseSelect,
  ButtonType
} from '@/components';
import { TableColumnType } from '@/components/common/table/interfaces/TableColumnModel';
import { useStudyStore } from '@/stores/study';
import { useFormStore } from '@/stores/forms';
import { useEntryListStore } from '@/stores/entryList';
import { CaseModel, EntryModel, FormModel } from '@/models';
import { DATA_VIEW_FILTER, routeConstants, formattedDate, DATE_FORMAT_2, LocalStorageUtils } from '@/utils';
import CustomButton from '@/components/common/CustomButton.vue';
import { useEntryStore } from '@/stores/entry';

const selectedEntries = defineModel({
  type: Object as PropType<EntryModel[]>,
  required: true
});

const entryListStore = useEntryListStore();
const entryStore = useEntryStore();
const formStore = useFormStore();
const studyStore = useStudyStore();

const loadEntries = () => {
  if (LocalStorageUtils.getCurrentDataViewFilter() === DATA_VIEW_FILTER.ENTRIES && !entryListStore.selectedCase) {
    entryListStore.getStudyEntries();
  } else {
    entryListStore.getEntries();
  }
};

onMounted(() => {
  loadEntries();
});

const onCaseChanged = (entryModel: EntryModel, caseModel?: CaseModel) => {
  entryListStore.updateEntryCase(entryModel.identifier, caseModel?.identifier);
};

const router = useRouter();
const handleEntryClick = (entry: EntryModel) => {
  if (entryListStore.selectedCase) {
    router.push({
      name: routeConstants.CASE_ENTRY.name,
      params: {
        identifier: entryListStore.selectedCase.identifier,
        formIdentifier: entry.formIdentifier,
        entryIdentifier: entry.identifier
      }
    });
  } else {
    router.push({
      name: routeConstants.ENTRY.name,
      params: {
        formIdentifier: entry.formIdentifier,
        entryIdentifier: entry.identifier
      }
    });
  }
};

const handleFormOpen = (form: FormModel, caseId?: string) => {
  if (entryListStore.selectedCase) {
    router.push({
      name: routeConstants.CASE_NEW_ENTRY.name,
      params: {
        identifier: entryListStore.selectedCase.identifier,
        formIdentifier: form.identifier
      }
    });
  } else if (caseId) {
    router.push({
      name: routeConstants.CASE_NEW_ENTRY.name,
      params: {
        identifier: caseId,
        formIdentifier: form.identifier
      }
    });
  } else {
    router.push({
      name: routeConstants.NEW_ENTRY.name,
      params: {
        formIdentifier: form.identifier
      }
    });
  }
};

const showEntryItemFieldAnswerInTitle = (entry: EntryModel) => {
  const dataObjectKey = `${studyStore.selectedStudy?.flags?.customDataDictionary}_DATA`;
  if (studyStore.selectedStudy?.flags && Object.keys(studyStore.selectedStudy?.flags).find(key => key == dataObjectKey)) {
    //@ts-expect-error: dynamic data for PUMP
    const fieldId = studyStore.selectedStudy?.flags[dataObjectKey][entry.formIdentifier];
    return entry.items && fieldId ? `${entry.formTitle} (${entry.items[fieldId]})` : entry.formTitle;
  }
  return entry.formTitle;
};

const copyEntry = async (entry: EntryModel) => {
  const formModel = formStore.formList.find(form => form.identifier === entry.formIdentifier);
  if (formModel) {
    entryStore.copiedEntryData = entry;
    // open the copied entry form in the context of the entry's case if it exists
    handleFormOpen(formModel, entry.caseIdentifier);
  }
};
</script>

<template>
  <div class="entry-list-root">
    <Table :columns="entryListStore.tableColumns"
      :data-list="entryListStore.entryList"
      :loading-state="entryListStore.entriesLoader.isLoading"
      :selection="!entryListStore.selectedCase?.archivedAt && !entryListStore.selectedCase?.lockedAt"
      v-model:selectedRows="selectedEntries"
      :totalRecords="entryListStore.totalEntriesCount"
      @onPageChange="entryListStore.onPageChange"
      @onRowClick="handleEntryClick"
      @onSortFieldChange="entryListStore.setSort"
      @onSortOrderChange="entryListStore.setSortOrder"
      presort="createdAt">
      <template #bodyContent="{ data, col }">
        <div class="entry-item"
          v-if="col.type === TableColumnType.TEXT_WTIH_ICON">
          <TableIconText :text="col.key == 'formTitle' ? showEntryItemFieldAnswerInTitle(data) : data[col.key]" :icon="col.icon" />
          <FontAwesomeIcon :icon="[`fas`, `lock`]"
            v-if="data['lockedAt']"
            class="lock"/>
        </div>

        <div v-if="col.type === TableColumnType.TEXT">
          {{
            col.key === "createdAt" || col.key === "updatedAt"
              ? formattedDate(data[col.key], DATE_FORMAT_2)
              : data[col.key]
          }}
        </div>

        <div v-if="col.type === TableColumnType.SQAURE_BUTTON_WITH_ICON" class="hover-buttons">
          <CustomButton text="Copy entry"
            :icon="{style: 'far', icon: 'clone'}" 
            :type="ButtonType.VERTICAL_OUTLINED"
            @onClick="copyEntry(data)"/>
        </div>

        <EntryCaseSelect :entryModel="data"
          :caseModel="data[col.key]"
          v-if="col.type === TableColumnType.SELECT"
          @onCaseChanged="onCaseChanged"/>

        <UserAvatar :avatarUrl="data.creatorAvatar"
          :userName="data[col.key] ?? 'System Generated'"
          :size="28"
          v-if="col.type === TableColumnType.USER"/>
      </template>
      <template #emptyContent>
        <div class="empty-content-container">
          <div class="empty-content">
            <FontAwesomeIcon :icon="[`fal`, 'file']" class="file-icon" />
            <h3 class="no-entries-label">No entries created yet</h3>
            <div class="new-entry">
              <div class="new-entry-text">To create an entry click on</div>
              <NewEntryPopup :forms="formStore.formList"
                :entries="entryListStore.entryList"
                :margin-left="16"
                @onFormClick="handleFormOpen"
                v-if="
                  studyStore.selectedStudy?.longitudinal === true && !entryListStore.selectedCase?.archivedAt
                    ? selectedEntries.length === 0 && entryListStore.selectedCase && entryListStore.selectedCase?.lockedAt == undefined
                    : selectedEntries.length === 0 && !studyStore.selectedStudy?.longitudinal
                "/>
            </div>
          </div>
        </div>
      </template>
    </Table>
  </div>
</template>

<style scoped lang="scss">
.entry-list-root {
  height: 100%;
}
.entry-item {
  display: flex;
  align-items: center;

  .lock {
    margin-left: 8px;
    font-size: 16px;
  }
}
.empty-content-container {
  padding: 50px 0px;
  display: flex;
  justify-content: center;
  align-items: center;
  .empty-content {
    text-align: center;
    .file-icon {
      font-size: 100px;
      margin: 16px 0px;
    }

    .no-entries-label {
      font-weight: bold;
    }

    .new-entry {
      margin: 16px 0px;
      display: flex;
      align-items: center;
    }
  }
}
// This is for new entry button, but this might cause issues if we add a new menu in this component.
:deep(.p-menu, .p-menu-list) {
  max-height: 50vh;
  max-width: 400px;
  overflow-y: auto;
}
.hover-buttons {
  visibility: hidden;
}
:deep(.p-row-even) {
  cursor: pointer;
  &:hover {
    .hover-buttons {
      visibility: visible;
    }
  }
}
:deep(.p-row-odd) {
  cursor: pointer;
  &:hover {
    .hover-buttons {
      visibility: visible;
    }
  }
}
</style>
